import React, { useCallback, useEffect, useState } from 'react';
import { API_ENDPOINTS } from '../../config';
import { getBearerAuthHeaders } from '../../utils/authFetchHeaders';

/**
 * Insights settings: which prop firm plan each account is on (its days are
 * then read against the plan's daily loss limit and consistency rule), and the
 * commission for NinjaTrader add-on trades (its fills don't carry it).
 */

const jsonHeaders = () => ({ ...getBearerAuthHeaders(), 'Content-Type': 'application/json' });

async function send(url, method, body) {
  const res = await fetch(url, { method, headers: jsonHeaders(), body: body ? JSON.stringify(body) : undefined });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.success) throw new Error(data.error || 'Couldn’t save that.');
  return data;
}

function PlanPicker({ account, firms, onSaved, onError }) {
  const current = account.propFirm || {};
  const [firmKey, setFirmKey] = useState(current.firmKey || '');
  const [planId, setPlanId] = useState(current.planId || '');
  const [size, setSize] = useState(current.accountSize ? String(current.accountSize) : '');
  const [busy, setBusy] = useState(false);
  const firm = firms.find((f) => f.key === firmKey);
  const plan = firm?.plans.find((p) => p.id === planId);

  const save = async (next) => {
    setBusy(true);
    try {
      await send(API_ENDPOINTS.tradeInsightsPropFirm, 'PUT', { accountName: account.name, ...next });
      onSaved();
    } catch (err) {
      onError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="tis-plan">
      <span className="tis-account" title={account.name}>{account.name}</span>
      <select aria-label={`Prop firm for ${account.name}`} value={firmKey} disabled={busy} onChange={(e) => {
        const v = e.target.value;
        setFirmKey(v); setPlanId(''); setSize('');
        if (!v) save({ firmKey: null });
      }}>
        <option value="">Not a prop firm account</option>
        {firms.map((f) => <option key={f.key} value={f.key}>{f.firm}</option>)}
      </select>
      {firm ? (
        <select aria-label="Plan" value={planId} disabled={busy} onChange={(e) => { setPlanId(e.target.value); setSize(''); }}>
          <option value="">Plan…</option>
          {firm.plans.map((p) => <option key={p.id} value={p.id}>{p.plan}</option>)}
        </select>
      ) : null}
      {plan ? (
        <select aria-label="Account size" value={size} disabled={busy} onChange={(e) => {
          setSize(e.target.value);
          if (e.target.value) save({ firmKey, planId, accountSize: Number(e.target.value) });
        }}>
          <option value="">Size…</option>
          {plan.sizes.map((z) => <option key={z.accountSize} value={z.accountSize}>{z.label}</option>)}
        </select>
      ) : null}
    </li>
  );
}

export default function TradeInsightsSettings({ onChanged }) {
  const [settings, setSettings] = useState(null);
  const [firms, setFirms] = useState([]);
  const [commission, setCommission] = useState('');
  const [error, setError] = useState('');
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    try {
      const [s, f] = await Promise.all([
        fetch(API_ENDPOINTS.tradeInsightsSettings, { headers: getBearerAuthHeaders() }).then((r) => r.json()),
        fetch(API_ENDPOINTS.propFirmPresets).then((r) => r.json()).catch(() => ({ firms: [] })),
      ]);
      if (!s.success) throw new Error(s.error || 'Couldn’t load settings.');
      setSettings(s);
      setCommission(s.addonCommissionPerSide != null ? String(s.addonCommissionPerSide) : '');
      setFirms(f.firms || []);
    } catch (err) {
      setError(err.message);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saved = (text) => {
    setError('');
    setNote(text);
    onChanged?.();
    load();
  };

  const saveCommission = async (e) => {
    e.preventDefault();
    try {
      await send(API_ENDPOINTS.tradeInsightsSettings, 'PUT', { addonCommissionPerSide: commission === '' ? null : Number(commission) });
      saved('Saved. Insights will use it next time they’re built.');
    } catch (err) {
      setError(err.message);
    }
  };

  if (!settings) return error ? <p className="ti-error">{error}</p> : <p className="ti-empty">Loading settings…</p>;
  return (
    <div className="tis">
      {error ? <p className="ti-error">{error}</p> : null}
      {note ? <p className="tis-note">{note}</p> : null}

      {settings.hasAddon ? (
        <form className="tis-row" onSubmit={saveCommission}>
          <label htmlFor="tis-commission">
            <strong>NinjaTrader add-on commission</strong>
            <span>Per contract, per side. The add-on’s fills don’t include it, so it’s taken off here.</span>
          </label>
          <div className="tis-inline">
            <span>$</span>
            <input id="tis-commission" type="number" min="0" max="50" step="0.01" value={commission} placeholder="0.00" onChange={(e) => setCommission(e.target.value)} />
            <button type="submit" className="ti-btn">Save</button>
          </div>
        </form>
      ) : null}

      <div className="tis-row">
        <div>
          <strong>Prop firm accounts</strong>
          <span>Pick the plan an account is on to see its days against that plan’s daily loss limit and consistency rule.</span>
        </div>
        {settings.accounts.length ? (
          <ul className="tis-plans">
            {settings.accounts.map((a) => (
              <PlanPicker key={a.name} account={a} firms={firms} onSaved={() => saved('Saved.')} onError={setError} />
            ))}
          </ul>
        ) : <p className="ti-empty">No accounts yet.</p>}
      </div>
    </div>
  );
}
